/**
 * Application Entry Point
 * Initializes providers and renders root component
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryProvider } from './providers/QueryProvider';
import { AuthProvider } from './features/auth/AuthProvider';
import { initializeApiClient, getApiBaseUrl } from './api/client';
import { notifyRateLimited } from './lib/rateLimitNotice';
import AppRoutes from './routes';
import '@/styles/globals.css';

// Initialize API client
initializeApiClient({
  baseUrl: getApiBaseUrl(),
  getAccessToken: () => {
    // Zustand persist stores state under 'splister-auth' key as JSON
    const stored = localStorage.getItem('splister-auth');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.state?.accessToken || null;
      } catch {
        return null;
      }
    }
    return null;
  },
  onUnauthorized: () => {
    // Called only after ApiClient has exhausted retries on transient
    // "Spotify token not found yet" errors - this means the JWT itself
    // is genuinely invalid/expired/revoked. Clear auth state and redirect.
    localStorage.removeItem('splister-auth');
    window.location.href = '/login';
  },
  onForbidden: (code: string) => {
    // Handle whitelist check - redirect to invitation required page
    if (code === 'NOT_WHITELISTED') {
      localStorage.removeItem('splister-auth');
      window.location.href = '/invitation-required';
    }
  },
  onRateLimited: (info) => {
    notifyRateLimited(info.reset * 1000);
    console.warn('Rate limit exceeded:', info);
  },
});

// Render app
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <QueryProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </QueryProvider>
    </BrowserRouter>
  </StrictMode>
);
